/* global HTMLDivElement, Event, CustomEvent */
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { BookMarked, BookOpen, Calendar, CheckCircle, CheckCircle2, ChevronDown, Flame, FolderKanban, Goal, Inbox, Keyboard, LayoutGrid, Plus, Search, Settings, Sun, Trash2, Wallet, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { SyncState } from "../types";
import { useEventReminders } from "../hooks/use-event-reminders";
import { useBudgetAlertNotifications } from "../hooks/use-budget-alerts";
import { useAutoCurrencyRates } from "../hooks/use-auto-currency-rates";
import { refreshPushSubscription } from "../lib/push-reminders";
import { EntityFormModal } from "./entity-form";
import type { EntityKind, FormTarget } from "./entity-form";
import { SearchModal } from "./search-modal";
import { OnboardingModal } from "./onboarding-modal";
import { WhatsNewModal } from "./whats-new-modal";
import { KeyboardShortcuts } from "./keyboard-shortcuts";
import { getLastSeenVersion, isOnboardingDone, setLastSeenVersion, getUiTheme } from "../lib/app-prefs";
import { BackupReminder } from "./backup-reminder";
import { ViewErrorBoundary } from "./view-error-boundary";
import { AssistantLauncher } from "./assistant-launcher";
import { useFocusTrap } from "../lib/focus-trap";
import { TodayView } from "./today-view";
import { AuraTodayView } from "./aura-today-view";
import { InboxView } from "./inbox-view";
import { TasksView } from "./tasks-view";
import { shouldShowFirstSyncSkeleton } from "./first-sync-loading";

// Code-split: secondary views load on first visit (named exports → lazy default shim).
// Today/Inbox/Tasks stay eager — they are the landing view and the most-used flows.
const CalendarView = lazy(() => import("./calendar-view").then((m) => ({ default: m.CalendarView })));
const ProjectsView = lazy(() => import("./projects-view").then((m) => ({ default: m.ProjectsView })));
const GoalsView = lazy(() => import("./goals-view").then((m) => ({ default: m.GoalsView })));
const HabitsView = lazy(() => import("./habits-view").then((m) => ({ default: m.HabitsView })));
const NotesView = lazy(() => import("./notes-view").then((m) => ({ default: m.NotesView })));
const FinanceView = lazy(() => import("./finance-view").then((m) => ({ default: m.FinanceView })));
const JournalView = lazy(() => import("./journal-view").then((m) => ({ default: m.JournalView })));
// Settings pulls in AI settings, data merge/import + conflict modal — load on open.
const SettingsModal = lazy(() => import("./settings-modal").then((m) => ({ default: m.SettingsModal })));

type NavItem = { id: string; label: string; icon: LucideIcon };
const NAV: NavItem[] = [
  { id: "today", label: "Hôm nay", icon: Sun },
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "tasks", label: "Việc cần làm", icon: CheckCircle },
  { id: "projects", label: "Dự án", icon: FolderKanban },
  { id: "goals", label: "Mục tiêu", icon: Goal },
  { id: "habits", label: "Thói quen", icon: Flame },
  { id: "calendar", label: "Lịch", icon: Calendar },
  { id: "notes", label: "Ghi chú", icon: BookOpen },
  { id: "finance", label: "Tài chính", icon: Wallet },
  // "More" group — secondary views not in mobile bottom-nav pinned list
  { id: "journal", label: "Nhật ký", icon: BookMarked },
];
// Mobile pins the GTD daily loop: capture (Inbox) + plan (Today/Calendar) + do (Tasks).
// These 4 surround the center raised "+" in the bottom nav.
const MOBILE_PINNED = ["today", "inbox", "tasks", "calendar"] as const;
const MOBILE: NavItem[] = NAV.filter((n) => (MOBILE_PINNED as readonly string[]).includes(n.id));
// "Khác" overflow = every destination NOT already pinned in the bottom nav
// (Dự án/Mục tiêu/Thói quen/Ghi chú/Tài chính/Nhật ký). Settings/Search/Shortcuts
// are appended separately so the sheet is a real overflow, not a duplicate.
const MOBILE_MORE: NavItem[] = NAV.filter((n) => !(MOBILE_PINNED as readonly string[]).includes(n.id));
const TITLES = Object.fromEntries(NAV.map((n) => [n.id, n.label]));
const CREATE: Array<{ kind: EntityKind; label: string; icon: LucideIcon }> = [
  { kind: "task", label: "Việc cần làm", icon: CheckCircle },
  { kind: "note", label: "Ghi chú", icon: BookOpen },
  { kind: "transaction", label: "Giao dịch", icon: Wallet },
  { kind: "project", label: "Dự án", icon: FolderKanban },
  { kind: "goal", label: "Mục tiêu", icon: Goal },
  { kind: "habit", label: "Thói quen", icon: Flame },
  { kind: "event", label: "Lịch", icon: Calendar },
];

// ── A8: What's New version gate ──────────────────────────────────────────────
// F17: version comes from the build (vite define ← package.json / VITE_APP_VERSION,
// same source the SW cache-buster in main.tsx uses) — no more hardcoded string.
// Each release bump re-triggers the spotlight once; dev fallback never matches a
// stored production version twice.
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";

// ── B10: first-sync skeleton ──────────────────────────────────────────────────

/** Lightweight skeleton shown while the first cloud sync is in-flight and collections are empty. */
function SkeletonTiles() {
  const pulse = "animate-pulse motion-reduce:animate-none";
  const bar = (w: string) => <div className={`h-3 rounded-full bg-ink-200 ${w} ${pulse}`} />;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true" aria-label="Đang tải dữ liệu…">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-3xl bg-white p-5 shadow-sm">
          <div className={`mb-4 h-4 w-1/3 rounded-full bg-ink-200 ${pulse}`} />
          <div className="space-y-2.5">
            {bar("w-full")}
            {bar("w-4/5")}
            {bar("w-2/3")}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── B3: syncState → visual indicator mapping ─────────────────────────────────

type SyncIndicator = { label: string; dotClass: string; pulse: boolean };

function getSyncIndicator(state: SyncState): SyncIndicator {
  switch (state) {
    case "synced":     return { label: "Đã đồng bộ",    dotClass: "bg-emerald-500", pulse: false };
    case "connecting": return { label: "Đang đồng bộ",  dotClass: "bg-amber-400",   pulse: true  };
    case "offline":    return { label: "Ngoại tuyến",   dotClass: "bg-ink-300",     pulse: false };
    case "error":      return { label: "Lỗi đồng bộ",   dotClass: "bg-rose-500",    pulse: false };
    case "auth":       return { label: "Chưa đăng nhập",dotClass: "bg-ink-300",     pulse: false };
    case "local":
    default:           return { label: "Lưu trên máy",  dotClass: "bg-ink-300",     pulse: false };
  }
}

export function BentoShell({ api }: { api: LifeOsApi }) {
  // In-app reminders (tab open) — fire browser notifications for today's events.
  useEventReminders(api.state.events);
  // D52: bắn thông báo khi 1 ngân sách vừa chạm ngưỡng / vượt hạn mức trong phiên.
  useBudgetAlertNotifications(api);
  // Web Push: keep this device's stored subscription fresh on sign-in (best-effort;
  // no-op unless supported + permission granted + signed in). Server scheduler delivers.
  useEffect(() => {
    void refreshPushSubscription(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.auth.user?.uid]);

  // Tỷ giá tự cập nhật ngầm khi có ví ngoại tệ (thiếu/cũ >24h) — không có UI riêng.
  useAutoCurrencyRates(api);

  const [view, setView] = useState("today");
  const [uiTheme, setUiTheme] = useState<"classic" | "aura">(() => getUiTheme());

  useEffect(() => {
    const handleUiThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent<"classic" | "aura">;
      setUiTheme(customEvent.detail);
    };
    window.addEventListener("ui-theme-changed", handleUiThemeChange);
    return () => {
      window.removeEventListener("ui-theme-changed", handleUiThemeChange);
    };
  }, []);

  const [confirm, setConfirm] = useState<(() => void) | null>(null);
  const [toast, setToast] = useState<{ msg: string; undo?: () => void } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const [more, setMore] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [fab, setFab] = useState(false);
  const [form, setForm] = useState<FormTarget>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // B4: shortcuts overlay controllable from header button
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [focus, setFocus] = useState<{ view: string; id: string } | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboardingDone());
  // P1 safety: never show first-run onboarding to a signed-in user. Their cloud data is
  // authoritative and may still be syncing in; "bắt đầu trống" would clobber it via replaceState.
  useEffect(() => {
    if (api.auth.user) setShowOnboarding(false);
  }, [api.auth.user]);
  // A8: What's New — show only once per APP_VERSION, and never stack with onboarding.
  // Initialise lazily: only unseen version AND onboarding already done triggers it.
  const [showWhatsNew, setShowWhatsNew] = useState(
    () => isOnboardingDone() && getLastSeenVersion() !== APP_VERSION,
  );
  const handleWhatsNewClose = () => {
    setLastSeenVersion(APP_VERSION);
    setShowWhatsNew(false);
  };
  const confirmRef = useRef<HTMLDivElement>(null);
  useFocusTrap(confirmRef, Boolean(confirm)); // a11y: trap focus in the delete dialog

  // Toast with optional Undo action (D33: single last-action undo via state snapshot).
  const showToast = (msg: string, undo?: () => void) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), undo ? 6000 : 2800);
  };
  const notify = (msg: string) => showToast(msg);
  const askDelete = (fn: () => void) => setConfirm(() => fn);
  const doDelete = () => {
    const snapshot = api.state; // immutable pre-delete snapshot for undo
    confirm?.();
    setConfirm(null);
    showToast("Đã xoá", () => {
      window.clearTimeout(toastTimer.current);
      api.actions.replaceState(snapshot);
      setToast(null);
    });
  };
  const go = (v: string) => { setView(v); setMore(false); setFocus(null); };
  // a11y: Esc / global shortcut to dismiss any open overlay.
  const closeAll = () => { setConfirm(null); setForm(null); setSearchOpen(false); setSettingsOpen(false); setMore(false); setCreateOpen(false); setFab(false); setShortcutsOpen(false); };
  const openForm = (kind: EntityKind, item?: Record<string, unknown>) => { setForm({ kind, item }); setCreateOpen(false); setFab(false); };
  // Cross-navigation: open a related entity's detail in its view (clickable relations).
  const KIND_VIEW: Record<string, string> = { task: "tasks", project: "projects", goal: "goals", habit: "habits", note: "notes", transaction: "finance", event: "calendar" };
  const openEntity = (kind: string, id: string) => { setView(KIND_VIEW[kind] ?? "today"); setMore(false); setSearchOpen(false); setFocus({ view: KIND_VIEW[kind] ?? "today", id }); };
  const focusId = (v: string) => (focus && focus.view === v ? focus.id : undefined);

  const now = new Date();
  const dateLabel = now.toLocaleDateString("vi-VN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).toUpperCase();
  // Mobile-short: "T2, 16/6" — weekday abbrev + day/month, no wrap.
  const shortWeekday = now.toLocaleDateString("vi-VN", { weekday: "short" });
  const dateLabelShort = `${shortWeekday}, ${now.getDate()}/${now.getMonth() + 1}`;
  const viewProps = { api, onForm: openForm, onDelete: askDelete, openEntity };

  // B3: derive sync indicator once per render
  const syncInfo = getSyncIndicator(api.syncState);

  // B10: show skeleton only for a truly empty signed-in first sync. Background
  // push/connect states must not hide every tab once local user data exists.
  const isFirstSyncLoading = shouldShowFirstSyncSkeleton(api.state, api.syncState, api.auth.user != null);

  return (
    <div className="min-h-screen">
      {uiTheme === "aura" && (
        <>
          <div className="aura-blob aura-blob-1" />
          <div className="aura-blob aura-blob-2" />
          <div className="aura-blob aura-blob-3" />
        </>
      )}
      <div className="mx-auto flex max-w-[1340px]">
        {/* SIDEBAR */}
        <aside className={`sticky top-0 hidden h-screen w-[228px] shrink-0 flex-col gap-5 px-4 py-6 ${uiTheme === "aura" ? "" : "lg:flex"}`}>
          <div className="flex items-center gap-2.5 px-2"><div className="gbrand grid h-10 w-10 place-items-center rounded-2xl font-display text-base font-bold text-white">OS</div><h1 className="font-display text-[17px] font-bold">Life OS</h1></div>
          <button onClick={() => go("inbox")} className="gbrand flex items-center justify-center gap-1.5 rounded-2xl py-2.5 text-[13px] font-bold text-white"><Zap className="h-4 w-4" />Ghi nhanh</button>
          <nav aria-label="Điều hướng chính" className="flex flex-col gap-1 text-[13.5px]">
            {NAV.map((n) => {
              const active = n.id === view;
              return (
                <button key={n.id} onClick={() => go(n.id)} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 font-semibold transition ${active ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:bg-white/60"}`}>
                  <n.icon className={`h-[18px] w-[18px] ${active ? "text-brand-600" : ""}`} />{n.label}
                </button>
              );
            })}
          </nav>
          {/* B3: sidebar sync status — driven from api.syncState */}
          <button
            onClick={() => {
              // Red/offline state → retry sync immediately, then open diagnostics.
              if (api.syncState === "error" || api.syncState === "offline") api.retrySync();
              setSettingsOpen(true);
            }}
            title={api.syncState === "error" || api.syncState === "offline" ? "Bấm để thử đồng bộ lại" : undefined}
            aria-label="Cài đặt và đồng bộ"
            className="mt-auto flex items-center gap-2 rounded-2xl bg-white p-3 text-left"
          >
            <span className={`h-2 w-2 rounded-full ${syncInfo.dotClass}${syncInfo.pulse ? " animate-pulse" : ""}`} />
            <p className="flex-1 text-[12px] font-bold">{syncInfo.label}</p>
            <Settings className="h-4 w-4 text-ink-400" />
          </button>
        </aside>

        {/* MAIN */}
        <main className={`relative min-w-0 flex-1 px-5 pb-28 pt-6 sm:px-8 ${uiTheme === "aura" ? "lg:pb-28" : "lg:pb-10"}`}>
          {/* P1-5: persisting to localStorage failed (quota/private mode) — data lives only in memory */}
          {api.storageError && (
            <div role="alert" className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] font-semibold text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
              ⚠️ Không lưu được dữ liệu vào bộ nhớ máy (bộ nhớ đầy hoặc chế độ riêng tư). Thay đổi mới có thể mất khi đóng ứng dụng — hãy xuất sao lưu trong Cài đặt hoặc giải phóng dung lượng.
            </div>
          )}
          {/* D37: weekly backup reminder (renders nothing unless due) */}
          <BackupReminder api={api} />
          <header className="mb-5 flex items-end justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* B3: mobile OS badge area — sync dot added next to badge */}
              <div className="relative lg:hidden">
                <div className="gbrand grid h-9 w-9 place-items-center rounded-xl font-display text-sm font-bold text-white">OS</div>
                <span
                  className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${syncInfo.dotClass}${syncInfo.pulse ? " animate-pulse" : ""}`}
                  aria-label={syncInfo.label}
                  title={syncInfo.label}
                />
              </div>
              <div>
                {/* Date: short, non-wrapping on mobile; full uppercase on desktop. */}
                <p className="text-[12px] font-bold text-brand-500"><span className="lg:hidden">{dateLabelShort}</span><span className="hidden lg:inline">{dateLabel}</span></p>
                <h2 className="font-display text-[28px] font-bold tracking-tight">{TITLES[view]}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* MOBILE header actions — Search + Khác (overflow) + LifeAI as icon buttons.
                  Settings/Shortcuts live inside the "Khác" sheet. Create lives in the
                  bottom-nav center "+"; no header create dropdown on mobile. */}
              <button onClick={() => setSearchOpen(true)} aria-label="Tìm kiếm" className="grid h-9 w-9 place-items-center rounded-xl bg-white text-ink-500 sm:hidden"><Search className="h-4 w-4" /></button>
              <button onClick={() => setMore(true)} aria-label="Tất cả mục" className="grid h-9 w-9 place-items-center rounded-xl bg-white text-ink-500 lg:hidden"><LayoutGrid className="h-4 w-4" /></button>
              {/* LifeAI: renders mobile header icon (lg:hidden) + desktop floating pill (fixed). Self-hides when AI unavailable. */}
              <AssistantLauncher api={api} />
              {/* Desktop search + shortcuts + settings */}
              <div className="hidden items-center gap-2 sm:flex">
                <button onClick={() => setSearchOpen(true)} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[13px] text-ink-500"><Search className="h-4 w-4" />Tìm</button>
                {/* B4: Phím tắt — icon-only (low-frequency; nhãn chiếm chỗ vô ích trên header) */}
                <button onClick={() => setShortcutsOpen(true)} aria-label="Phím tắt" title="Phím tắt bàn phím" className="grid h-9 w-9 place-items-center rounded-xl bg-white text-ink-500"><Keyboard className="h-4 w-4" /></button>
                <button onClick={() => setSettingsOpen(true)} aria-label="Cài đặt" className="grid h-9 w-9 place-items-center rounded-xl bg-white text-ink-500"><Settings className="h-4 w-4" /></button>
                <div className="relative">
                  {/* "Tạo mới" là nút PHỤ (outline) — primary toàn cục duy nhất là "Ghi nhanh" ở sidebar;
                      mỗi view có primary ngữ cảnh riêng (vd "Ghi giao dịch"). Tránh 3 nút gradient trùng vai. */}
                  <button onClick={() => setCreateOpen((v) => !v)} className="flex items-center gap-1.5 rounded-xl border border-ink-200 bg-white px-4 py-2 text-[13px] font-bold text-ink-700 hover:bg-ink-50"><Plus className="h-4 w-4 text-brand-600" />Tạo mới<ChevronDown className="h-3.5 w-3.5" /></button>
                  {createOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setCreateOpen(false)} />
                      <div className="absolute right-0 z-50 mt-2 w-56 rounded-2xl border border-ink-200 bg-white/95 p-2 shadow-[0_20px_60px_rgba(31,29,43,0.16)] ring-1 ring-white/70 backdrop-blur">
                        {CREATE.map((c) => (
                          <button key={c.kind} onClick={() => openForm(c.kind)} className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] font-semibold text-ink-700 transition hover:bg-brand-50 hover:text-brand-600">
                            <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-100 text-brand-600"><c.icon className="h-3.5 w-3.5" /></span>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </header>

          <section className="fade-in">
            {/* B10: first-sync skeleton — only while connecting with an authenticated user and empty data */}
            {isFirstSyncLoading ? (
              <SkeletonTiles />
            ) : (
              <ViewErrorBoundary>
              <Suspense fallback={<SkeletonTiles />}>
                {/* F7: theme branch hoisted here — exactly ONE Today view mounts
                    per render (classic no longer derives everything then defers
                    to Aura at the bottom). Both share useTodayOrchestration. */}
                {view === "today" && (uiTheme === "aura"
                  ? <AuraTodayView api={api} onGoto={go} onForm={openForm} />
                  : <TodayView {...viewProps} onGoto={go} />)}
                {view === "calendar" && <CalendarView {...viewProps} />}
                {view === "inbox" && <InboxView {...viewProps} />}
                {view === "tasks" && <TasksView {...viewProps} focusId={focusId("tasks")} />}
                {view === "projects" && <ProjectsView {...viewProps} focusId={focusId("projects")} />}
                {view === "goals" && <GoalsView {...viewProps} focusId={focusId("goals")} />}
                {view === "habits" && <HabitsView {...viewProps} focusId={focusId("habits")} />}
                {view === "notes" && <NotesView {...viewProps} focusId={focusId("notes")} />}
                {view === "finance" && <FinanceView {...viewProps} />}
                {view === "journal" && <JournalView api={api} openEntity={openEntity} />}
              </Suspense>
              </ViewErrorBoundary>
            )}
          </section>
        </main>
      </div>

      {/* MOBILE BOTTOM NAV — 4 pinned destinations around a center raised gbrand "+".
          Layout: [Today][Inbox]  (+)  [Tasks][Calendar].  "Khác" (overflow) lives in
          the header. Center "+" opens the "Tạo nhanh" sheet (single create path on mobile).
          pb-safe clears the iPhone home indicator. */}
      <nav aria-label="Điều hướng" className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-ink-200 bg-white/95 px-2 pt-1.5 backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-md items-center justify-around">
          {MOBILE.slice(0, 2).map((n) => {
            const active = n.id === view;
            return (
              <button key={n.id} onClick={() => go(n.id)} aria-label={n.label} aria-current={active ? "page" : undefined} className={`flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold ${active ? "text-brand-600" : "text-ink-500"}`}>
                <n.icon className="h-5 w-5" />{n.label}
              </button>
            );
          })}
          {/* Center raised create "+" — primary action, integrated into the bar. */}
          <button onClick={() => setFab(true)} aria-label="Tạo mới" className="gbrand -mt-6 grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-white shadow-[0_8px_24px_rgba(124,58,237,0.4)] ring-4 ring-white transition active:scale-95 dark:ring-[rgb(var(--surface))]"><Plus className="h-6 w-6" /></button>
          {MOBILE.slice(2, 4).map((n) => {
            const active = n.id === view;
            return (
              <button key={n.id} onClick={() => go(n.id)} aria-label={n.label} aria-current={active ? "page" : undefined} className={`flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold ${active ? "text-brand-600" : "text-ink-500"}`}>
                <n.icon className="h-5 w-5" />{n.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* FAB SHEET — opened by the center "+"; the single create path on mobile. */}
      {fab && (
        <div className="fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-sm lg:hidden" onClick={() => setFab(false)}>
          <div className="pb-safe-sheet fade-in absolute inset-x-0 bottom-0 rounded-t-3xl border-t border-ink-200 bg-white/95 p-4 shadow-[0_-20px_60px_rgba(31,29,43,0.18)] ring-1 ring-white/70 backdrop-blur" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ink-200" />
            <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-brand-500">Tạo nhanh</p>
            <div className="space-y-2">
              <button onClick={() => { go("inbox"); setFab(false); }} className="flex w-full items-center gap-3 rounded-2xl border border-ink-200 bg-white/80 px-4 py-3 text-[14px] font-semibold text-ink-700 shadow-sm transition hover:border-brand-100 hover:bg-brand-50 hover:text-brand-600"><span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-100 text-brand-600"><Zap className="h-4 w-4" /></span>Ghi nhanh</button>
              {CREATE.slice(0, 4).map((c) => (
                <button key={c.kind} onClick={() => openForm(c.kind)} className="flex w-full items-center gap-3 rounded-2xl border border-ink-200 bg-white/80 px-4 py-3 text-[14px] font-semibold text-ink-700 shadow-sm transition hover:border-brand-100 hover:bg-brand-50 hover:text-brand-600"><span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-100 text-brand-600"><c.icon className="h-4 w-4" /></span>{c.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MORE SHEET ("Khác") — real overflow: only the NON-pinned destinations
          (the 4 pinned ones live in the bottom nav) + Settings. Search is a header
          icon; Shortcuts is desktop-only (irrelevant on touch). */}
      {more && (
        <div className="fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-sm lg:hidden" onClick={() => setMore(false)}>
          <div className="pb-safe-sheet fade-in absolute inset-x-0 bottom-0 rounded-t-3xl border-t border-ink-200 bg-white/95 p-4 shadow-[0_-20px_60px_rgba(31,29,43,0.18)] ring-1 ring-white/70 backdrop-blur" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ink-200" />
            <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-brand-500">Khác</p>
            <div className="grid grid-cols-4 gap-3">
              {MOBILE_MORE.map((n) => {
                const active = n.id === view;
                return (
                  <button key={n.id} onClick={() => go(n.id)} aria-label={n.label} aria-current={active ? "page" : undefined} className={`flex flex-col items-center gap-1.5 rounded-2xl border py-3 text-[11px] font-semibold shadow-sm transition ${active ? "border-brand-200 bg-brand-50 text-brand-600" : "border-ink-200 bg-white/80 text-ink-700 hover:border-brand-100 hover:bg-brand-50 hover:text-brand-600"}`}>
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-100 text-brand-600"><n.icon className="h-5 w-5" /></span>{n.label}
                  </button>
                );
              })}
              {/* Tìm kiếm (đã có icon ở header) + Phím tắt (vô dụng trên cảm ứng) bỏ khỏi sheet mobile. */}
              <button onClick={() => { setSettingsOpen(true); setMore(false); }} aria-label="Cài đặt" className="flex flex-col items-center gap-1.5 rounded-2xl border border-ink-200 bg-white/80 py-3 text-[11px] font-semibold text-ink-700 shadow-sm transition hover:border-brand-100 hover:bg-brand-50 hover:text-brand-600">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-100 text-brand-600"><Settings className="h-5 w-5" /></span>Cài đặt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL KEYBOARD SHORTCUTS (a11y) — suppressed during first-run onboarding */}
      {/* B4: open/onOpenChange props wire the header "Phím tắt" button to the overlay */}
      {!showOnboarding && (
        <KeyboardShortcuts
          handlers={{
            onSearch: () => setSearchOpen(true),
            onNewTask: () => openForm("task"),
            onGoto: go,
            onCloseAll: closeAll,
          }}
          open={shortcutsOpen}
          onOpenChange={setShortcutsOpen}
        />
      )}

      {/* FIRST-RUN ONBOARDING */}
      {showOnboarding && <OnboardingModal api={api} onClose={() => setShowOnboarding(false)} />}

      {/* A8: WHAT'S NEW — only shown to returning users (onboarding done), never stacks with onboarding */}
      {!showOnboarding && showWhatsNew && (
        <WhatsNewModal version={APP_VERSION} onClose={handleWhatsNewClose} />
      )}

      {/* CREATE / EDIT FORM */}
      <EntityFormModal target={form} api={api} onClose={() => setForm(null)} onSaved={notify} />
      {searchOpen && <SearchModal api={api} onClose={() => setSearchOpen(false)} onGoto={go} onForm={openForm} />}
      {settingsOpen && (
        <Suspense fallback={null}>
          <SettingsModal api={api} onClose={() => setSettingsOpen(false)} />
        </Suspense>
      )}

      {/* DELETE CONFIRM */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm" onClick={() => setConfirm(null)} role="dialog" aria-modal="true" aria-label="Xác nhận xoá">
          <div ref={confirmRef} className="fade-in w-full max-w-sm rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-rose-50 text-rose-500"><Trash2 className="h-7 w-7" /></div>
            <h3 className="text-center font-display text-[20px] font-bold">Xoá mục này?</h3>
            <p className="mt-1 text-center text-[13px] text-ink-500">Bạn có thể hoàn tác trong 5 giây sau khi xoá.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button onClick={() => setConfirm(null)} className="rounded-xl bg-ink-200/60 py-3 text-[14px] font-bold text-ink-700">Huỷ</button>
              <button onClick={doDelete} className="rounded-xl bg-rose-500 py-3 text-[14px] font-bold text-white">Xoá</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className="mb-safe fixed bottom-24 left-1/2 z-[80] -translate-x-1/2 lg:mb-0 lg:bottom-6" role="status" aria-live="polite" aria-atomic="true">
          <div className="fade-in flex items-center gap-2.5 rounded-2xl bg-ink-900 px-4 py-3 text-[14px] font-semibold text-surface">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            {toast.msg}
            {toast.undo && (
              <button
                onClick={toast.undo}
                className="ml-1 rounded-lg bg-white/15 px-2.5 py-1 text-[12px] font-bold text-emerald-300 hover:bg-white/25"
              >
                Hoàn tác
              </button>
            )}
          </div>
        </div>
      )}

      {/* DESKTOP FLOATING DOCK — AURA GLASS ONLY */}
      {uiTheme === "aura" && (
        <div className="hidden lg:flex aura-dock">
          {NAV.map((n) => {
            const active = n.id === view;
            return (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                aria-label={n.label}
                title={n.label}
                className={`aura-dock-btn ${active ? "active" : ""}`}
              >
                <n.icon className="h-5 w-5" />
              </button>
            );
          })}
          <div className="h-6 w-px bg-ink-200 mx-1 dark:bg-slate-700" />
          <button
            onClick={() => setSettingsOpen(true)}
            aria-label="Cài đặt"
            title="Cài đặt"
            className="aura-dock-btn"
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={() => openForm("task")}
            aria-label="Ghi nhanh"
            title="Ghi nhanh"
            className="gbrand aura-dock-btn text-white rounded-xl"
          >
            <Zap className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
